import React, { useState } from 'react';
import { fnolAPI } from '../api';
import { sanitizeTimeForApi } from '../utils/time';

const FNOLSubmissionForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    // Loss Details
    lossName: '',
    claimant: '',
    dateOfLoss: '',
    timeOfLoss: '',
    causeOfLoss: 'Accident',
    lossDescription: '',
    estimatedLossCurrency: 'NA',
    estimatedLossAmount: '',
    lossLocation: '',
    gpsCoordinates: '',
    reportedDate: '',
    reportedBy: '',
    globalLostEvent: '',

    // Policy Details
    policyNumber: '',
    insuredName: '',
    inceptionDate: '',
    expirationDate: '',
    policyStatus: 'Active',
    lineOfBusiness: 'Property',
    brokerName: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submittedFNOL, setSubmittedFNOL] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatFormDataForAPI = () => {
    return {
      loss_name: formData.lossName,
      claimant: formData.claimant || null,
      date_of_loss: formData.dateOfLoss || null,
      time_of_loss: sanitizeTimeForApi(formData.timeOfLoss),
      cause_of_loss: formData.causeOfLoss,
      loss_description: formData.lossDescription || null,
      estimated_loss_currency: formData.estimatedLossCurrency,
      estimated_loss_amount: parseFloat(formData.estimatedLossAmount) || 0,
      loss_location: formData.lossLocation,
      gps_coordinates: formData.gpsCoordinates || null,
      report_date: formData.reportedDate ? new Date(formData.reportedDate).toISOString() : null,
      reported_by: formData.reportedBy || null,
      global_loss_details: formData.globalLostEvent || null,
      policy_number: formData.policyNumber || null,
      multiple_policy_number: null
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Basic validation - only validate truly required fields
    if (!formData.lossName || !formData.lossLocation || !formData.estimatedLossAmount || !formData.causeOfLoss) {
      setError('Please fill in all required fields (Loss Name, Cause of Loss, Location, and Estimated Amount)');
      setLoading(false);
      return;
    }

    try {
      // Submit FNOL
      const apiData = formatFormDataForAPI();
      const response = await fnolAPI.submitFNOL(apiData);

      setSubmittedFNOL(response);
      setSuccess(true);

      // Note: Claims team is automatically assigned by the backend based on Line of Business
      // No need to manually assign claims team

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      console.error('FNOL submission error:', err);
      
      // Handle different error response formats
      let errorMessage = 'Failed to submit FNOL';
      
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Handle validation errors array
          errorMessage = err.response.data.detail.map(error => 
            `${error.loc ? error.loc.join('.') : 'Field'}: ${error.msg}`
          ).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = 'Validation error occurred';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setError(null);
    try {
      const response = await fnolAPI.getFNOLs();
      if (response.success) {
        setError(null);
        alert('API connection successful! Found ' + response.count + ' FNOLs.');
      } else {
        setError('API connection failed: ' + response.message);
      }
    } catch (err) {
      setError('API connection failed: ' + (err.message || 'Unknown error'));
    } finally {
      setTestingConnection(false);
    }
  };

  if (success && submittedFNOL) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="text-success mb-3">
            <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem' }}></i>
          </div>
          <h5 className="card-title">FNOL Submitted Successfully!</h5>
          <p className="card-text">
            FNOL ID: <strong>{submittedFNOL.fnol_id}</strong>
          </p>
          <p className="text-muted small">
            Submitted at: {new Date(submittedFNOL.created_at).toLocaleString()}
          </p>
          <button
            className="btn btn-primary me-2"
            onClick={() => window.location.reload()}
          >
            View in Inbox
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={onCancel}
          >
            Submit Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Submit New FNOL</h5>
        <button
          type="button"
          className="btn btn-outline-info btn-sm"
          onClick={testConnection}
          disabled={testingConnection}
        >
          {testingConnection ? 'Testing...' : 'Test API'}
        </button>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Loss Details Section */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">Loss Details</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Loss Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lossName}
                  onChange={(e) => handleInputChange('lossName', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Claimant</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.claimant}
                  onChange={(e) => handleInputChange('claimant', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Date of Loss</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.dateOfLoss}
                  onChange={(e) => handleInputChange('dateOfLoss', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Time of Loss</label>
                <input
                  type="time"
                  className="form-control"
                  value={formData.timeOfLoss}
                  onChange={(e) => handleInputChange('timeOfLoss', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Cause of Loss *</label>
                <select
                  className="form-select"
                  value={formData.causeOfLoss}
                  onChange={(e) => handleInputChange('causeOfLoss', e.target.value)}
                  required
                >
                  <option value="Electrical Short Circuit">Electrical Short Circuit</option>
                  <option value="Fire">Fire</option>
                  <option value="Lightning">Lightning</option>
                  <option value="Explosion">Explosion</option>
                  <option value="Aircraft Damage">Aircraft Damage</option>
                  <option value="Flood">Flood</option>
                  <option value="Storm">Storm</option>
                  <option value="Cyclone">Cyclone</option>
                  <option value="Typhoon">Typhoon</option>
                  <option value="Hurricane">Hurricane</option>
                  <option value="Tornado">Tornado</option>
                  <option value="Heavy Rainfall">Heavy Rainfall</option>
                  <option value="Earthquake">Earthquake</option>
                  <option value="Tsunami">Tsunami</option>
                  <option value="Volcanic Eruption">Volcanic Eruption</option>
                  <option value="Burst Pipe">Burst Pipe</option>
                  <option value="Roof Leakage">Roof Leakage</option>
                  <option value="Burglary (Forced Entry)">Burglary (Forced Entry)</option>
                  <option value="Housebreaking">Housebreaking</option>
                  <option value="Robbery">Robbery</option>
                  <option value="Theft (No Forced Entry)">Theft (No Forced Entry)</option>
                  <option value="Vandalism">Vandalism</option>
                  <option value="Terrorism">Terrorism</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Loss Location *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lossLocation}
                  onChange={(e) => handleInputChange('lossLocation', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">GPS Coordinates</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.gpsCoordinates}
                  onChange={(e) => handleInputChange('gpsCoordinates', e.target.value)}
                  placeholder="Enter GPS coordinates"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Estimated Loss Amount *</label>
                <div className="input-group">
                  <select
                    className="form-select"
                    style={{ maxWidth: '120px' }}
                    value={formData.estimatedLossCurrency}
                    onChange={(e) => handleInputChange('estimatedLossCurrency', e.target.value)}
                  >
                    <option value="NA">NA</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="JPY">JPY (¥)</option>
                    <option value="INR">INR (₹)</option>
                  </select>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.estimatedLossAmount}
                    onChange={(e) => handleInputChange('estimatedLossAmount', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Reported Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.reportedDate}
                  onChange={(e) => handleInputChange('reportedDate', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Reported By</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.reportedBy}
                  onChange={(e) => handleInputChange('reportedBy', e.target.value)}
                  placeholder="Enter reporter name"
                />
              </div>
              <div className="col-12">
                <label className="form-label">Loss Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.lossDescription}
                  onChange={(e) => handleInputChange('lossDescription', e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Global Loss Event</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.globalLostEvent}
                  onChange={(e) => handleInputChange('globalLostEvent', e.target.value)}
                  placeholder="Enter global loss event (if applicable)"
                />
              </div>
            </div>
          </div>

          {/* Policy Details Section */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">Policy Details</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Policy Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.policyNumber}
                  onChange={(e) => handleInputChange('policyNumber', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Insured Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.insuredName}
                  onChange={(e) => handleInputChange('insuredName', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Inception Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.inceptionDate}
                  onChange={(e) => handleInputChange('inceptionDate', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Expiration Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.expirationDate}
                  onChange={(e) => handleInputChange('expirationDate', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Policy Status</label>
                <select
                  className="form-select"
                  value={formData.policyStatus}
                  onChange={(e) => handleInputChange('policyStatus', e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Line of Business</label>
                <select
                  className="form-select"
                  value={formData.lineOfBusiness}
                  onChange={(e) => handleInputChange('lineOfBusiness', e.target.value)}
                >
                  <option value="Property">Property</option>
                  <option value="Casualty">Casualty</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Marine">Marine</option>
                  <option value="Aviation">Aviation</option>
                  <option value="Credit & Surety">Credit & Surety</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Broker Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.brokerName}
                  onChange={(e) => handleInputChange('brokerName', e.target.value)}
                  placeholder="Enter broker name"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="d-flex gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Submitting...
                </>
              ) : (
                'Submit FNOL'
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FNOLSubmissionForm;
